import dayjs from 'dayjs';
import _ from 'lodash';
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';
import { CSV_DATA } from 'src/common/constants';
import { toastError, toastWarning } from 'src/common/helpers/Notifications';
import {
  chunkArray,
  fetchCombinedDataParallellyAsync,
  fetchDataParallellyAsync,
} from 'src/common/helpers/Utils';
import { downloadCSVFileData } from 'src/views/csv-data/CSVFileBuilder';
import ApiConstants from '../../../common/helpers/ApiConstants';
import * as requests from '../../../common/helpers/HTTPRequest';
import {
  CSV_DATA_MANAGEMENT_ACTIONS,
  downloadCSVDataError,
  downloadCSVDataSuccess,
  getCSVLoggersError,
  getCSVLoggersSuccess,
} from './actions';

function* getCSVLoggers() {
  try {
    const url = `${ApiConstants.BASE_URL}${ApiConstants.LOGGERS_MANAGEMENT}`;
    const response = yield call(requests.getRequest, url, {});

    yield put(getCSVLoggersSuccess(response));
  } catch (e) {
    console.log(e);
    toastError(e.message);
    yield put(getCSVLoggersError(e));
  }
}

function* downloadCSVData({ payload }) {
  try {
    const data = [];
    const { sortedDevices, filters, settings } = payload;

    const today = new Date();
    const todayStart = dayjs(today).startOf('D');
    const startDatetime = new Date(filters.startTime);
    const endDatetime = new Date(filters.endTime);
    const startISOStr = dayjs(
      startDatetime.setUTCHours(startDatetime.getUTCHours() + 9)
    )
      .toISOString()
      .replace(/\.\d+Z$/, '+09:00');
    let endISOStr = dayjs(
      endDatetime.setUTCHours(endDatetime.getUTCHours() + 9)
    )
      .toISOString()
      .replace(/\.\d+Z$/, '+09:00');

    let containsTodayData = false;
    // const missingTimeRangeRequest = [];

    for (const device of sortedDevices) {
      const tasks = [];
      const deviceId = device.deviceId;
      const combinedUrl = `${ApiConstants.BASE_URL}${ApiConstants.CSV_COMBINED_DATA}/${deviceId}?startTime=${startISOStr}&endTime=${endISOStr}`;

      tasks.push(
        fetchCombinedDataParallellyAsync(requests.getRequest, combinedUrl)
      );

      // fetch today data in case the time range contains today time
      if (dayjs(endDatetime).valueOf() >= todayStart.valueOf()) {
        let todayStartTime = dayjs(today).format('YYYY-MM-DD 00:00');

        if (dayjs(startDatetime).isAfter(todayStart)) {
          todayStartTime = filters.startTime;
        }

        const todayUrl = `${ApiConstants.BASE_URL}${ApiConstants.CSV_DATA_MANAGEMENT}/${deviceId}?startTime=$start$&endTime=$end$`;
        tasks.push(
          fetchDataParallellyAsync(
            requests.getRequest,
            todayUrl,
            dayjs(todayStartTime),
            dayjs(filters.endTime),
            60
          )
        );

        containsTodayData = true;
      }

      // const missingTimeRangeURL = `${
      //   ApiConstants.BASE_URL
      // }${ApiConstants.LOGGER_MISSING_TIME_RANGE.replace(
      //   '{:deviceId}',
      //   deviceId
      // )}/?startTime=${startISOStr}&endTime=${endISOStr}`;
      // missingTimeRangeRequest.push(
      //   requests.getRequest(missingTimeRangeURL, {})
      // );
      // }

      // waiting for all tasks done
      let responses = yield Promise.all(tasks);

      // const missingTimeRangeResponse = yield Promise.all(missingTimeRangeRequest);

      if (containsTodayData && responses?.length) {
        // 2 is combined data and today data
        responses = chunkArray(responses, 2);
      }

      let displayMaxRecExceedToast = false;
      let records = (responses || []).flat();

      // filter by start and end time & sno !=0 0
      records = records.filter(
        (x) =>
          x.sno !== 0 &&
          dayjs(x.lts) >= dayjs(filters.startTime) &&
          dayjs(x.lts) <= dayjs(filters.endTime)
      );

      // format datetime
      records.forEach((record) => {
        record.lts = dayjs(record.lts).format('YYYY/MM/DD HH:mm:ss');
      });

      // unique by time
      records = _.uniqBy(records, 'lts');

      // records are sorted by time and sequence number
      records = _.orderBy(records, ['lts', 'sno'], ['asc', 'asc']);
      // TODO why is the data being limited to 53k samples?
      if (records.length > CSV_DATA.MAX_RECORDS) {
        records = records.slice(0, CSV_DATA.MAX_RECORDS);
        displayMaxRecExceedToast = true;
      }

      // const deviceMissingRange = missingTimeRangeResponse[index];
      // const deviceMissingRange = [];

      let recordsUpdated = [];
      let previous = null;

      // push records into output set, and insert missing samples
      records.forEach((record, index) => {
        // this will skip first time
        if (previous !== null) {
          // if the sequence number of this record isn't incremental, fill missing data
          // iterate from previous to current until the sequence number matches
          const target = record['sno'];
          let current = previous['sno'];
          // TODO needs a stronger safety check for inf loop
          if (
            false ===
            (target >= 1 && target <= 53196 && current >= 1 && current <= 53196)
          ) {
            // broken sequence number
          } else {
            while (current + 1 !== target) {
              // generate a dummy record for missing sample and go to next sequence number (with wrap)
              // don't forget that sequence number starts at 1, not 0
              current += 1;
              if (current <= 53196) {
                const missing = {
                  lts: '',
                  ch1: '***',
                  ch2: '***',
                  ch3: '***',
                  af1: 0,
                  af2: 0,
                  af3: 0,
                  af4: 0,
                  evf: 0,
                  sno: current, // injecting expected sequence number
                  sty: 0,
                };
                recordsUpdated.push(missing);
              } else {
                current = 0; // this is fine because the tests are (+1)
              }
            }
          }
        }
        previous = record;
        recordsUpdated.push(record);
      });

      // cut of again after adding missing points
      if (recordsUpdated.length > CSV_DATA.MAX_RECORDS) {
        recordsUpdated = recordsUpdated.slice(0, CSV_DATA.MAX_RECORDS);
        displayMaxRecExceedToast = true;
      }

      data.push({
        logger: device,
        records: recordsUpdated,
      });

      if (displayMaxRecExceedToast) {
        toastWarning(
          '53,000データを超えているCSVデータは取得できませんでした。'
        );
      }
    }

    // [A6-4-1] (#5829): ロガー名称(クラウド) is only written when the company's
    // switch is ON; the flag rides on the profile from login/get_profile.
    const cloudLoggerNameEnabled = yield select(
      (state) => state.User?.userInfo?.cloudLoggerNameEnabled === true
    );
    downloadCSVFileData(settings, data, { cloudLoggerNameEnabled });

    yield put(downloadCSVDataSuccess());
  } catch (e) {
    console.log(e);
    toastError(e.message);
    yield put(downloadCSVDataError(e));
  }
}

export function* CSVDataManagementSaga() {
  yield takeLatest(CSV_DATA_MANAGEMENT_ACTIONS.GET_CSV_LOGGERS, getCSVLoggers);
  yield takeLatest(
    CSV_DATA_MANAGEMENT_ACTIONS.DOWNLOAD_CSV_DATA,
    downloadCSVData
  );
}

export default function* rootSaga() {
  yield all([fork(CSVDataManagementSaga)]);
}
